<template>
    <div class="pdf-audit-wrapper">
        <div class="pa-header">
            <div>
                <h1 class="pa-title">PDF Audit Log</h1>
                <p class="pa-subtitle">Cross-tenant record of every document processed on the platform</p>
            </div>

            <div class="pa-filters">
                <div class="filter-pill">
                    <label>Company</label>
                    <select v-model="selectedCompanyId" @change="fetchPdfs(1)">
                        <option value="">All Companies</option>
                        <option v-for="company in companies" :key="company.id" :value="company.id">
                            {{ company.name }}
                        </option>
                    </select>
                </div>

                <div class="filter-pill">
                    <label>Category</label>
                    <select v-model="selectedCategoryId" @change="fetchPdfs(1)">
                        <option value="">All Categories</option>
                        <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                            {{ cat.name }}
                        </option>
                    </select>
                </div>
            </div>
        </div>

        <div class="table-glass-card">
            <table class="premium-table">
                <thead>
                    <tr>
                        <th>File Name</th>
                        <th>User</th>
                        <th>Company</th>
                        <th>Category</th>
                        <th>Status</th>
                        <th>Uploaded At</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="pdf in pdfs" :key="pdf.id" class="table-row-hover">
                        <td class="file-name-cell">{{ pdf.pdf_name }}</td>
                        <td class="cell-secondary">{{ pdf.user ? pdf.user.name : 'User #' + pdf.user_id }}</td>
                        <td class="cell-secondary">{{ pdf.company ? pdf.company.name : 'N/A' }}</td>
                        <td>
                            <span class="category-tag">{{ getCategoryName(pdf.bid_category) }}</span>
                        </td>
                        <td>
                            <span class="status-badge active">
                                <i class="status-dot"></i>
                                {{ pdf.summary_status || 'ANALYZED' }}
                            </span>
                        </td>
                        <td class="cell-secondary">{{ formatDate(pdf.bid_opening_date) }}</td>
                    </tr>
                    <tr v-if="pdfs.length === 0">
                        <td colspan="6" class="empty-state">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                            </svg>
                            <p>No PDFs match these filters.</p>
                        </td>
                    </tr>
                </tbody>
            </table>
            <AppPagination :currentPage="currentPage" :totalPages="totalPages" @page-changed="fetchPdfs" />
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import AppPagination from './AppPagination.vue';

export default {
    name: 'PdfAudit',
    components: { AppPagination },
    data() {
        return {
            pdfs: [],
            companies: [],
            categories: [],
            selectedCompanyId: '',
            selectedCategoryId: '',
            currentPage: 1,
            totalPages: 1
        }
    },
    async mounted() {
        await Promise.all([this.fetchCompanies(), this.fetchPdfCategories()]);
        await this.fetchPdfs();
    },
    methods: {
        async fetchPdfs(page = 1) {
            try {
                this.currentPage = page;
                let url = `${this.$API_URL}/super_admin/pdfs?page=${page}&page_size=10`;
                if (this.selectedCompanyId) {
                    url += `&company_id=${this.selectedCompanyId}`;
                }
                if (this.selectedCategoryId) {
                    url += `&category_id=${this.selectedCategoryId}`;
                }
                const response = await axios.get(url, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` }
                });
                this.pdfs = response.data.items || [];
                this.totalPages = response.data.total_pages || 1;
            } catch (error) {
                console.error('Error fetching PDFs:', error);
            }
        },
        async fetchCompanies() {
            try {
                const response = await axios.get(`${this.$API_URL}/super_admin/companies?page_size=100`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` }
                });
                this.companies = response.data.items || [];
            } catch (error) {
                console.error('Error fetching companies:', error);
            }
        },
        async fetchPdfCategories() {
            try {
                const response = await axios.get(`${this.$API_URL}/super_admin/pdf-categories`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` }
                });
                this.categories = response.data || [];
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        },
        getCategoryName(catId) {
            if (!catId) return 'N/A';
            const cat = this.categories.find(c => String(c.id) === String(catId));
            return cat ? cat.name : 'Unknown';
        },
        formatDate(dateStr) {
            if (!dateStr) return 'N/A';
            const d = new Date(dateStr);
            return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
        }
    }
}
</script>

<style scoped>
.pdf-audit-wrapper {
    font-family: var(--font-sans);
}

.pa-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 1.5rem;
    margin-bottom: 2rem;
}

.pa-title {
    margin: 0;
    font-family: var(--font-display);
    font-size: var(--fs-h1);
    line-height: var(--lh-h1);
    font-weight: var(--fw-h1);
    color: var(--text-primary);
    letter-spacing: -0.02em;
}

.pa-subtitle {
    color: var(--text-secondary);
    margin: var(--space-heading-body) 0 0;
    font-size: var(--fs-sm);
}

.pa-filters {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.filter-pill {
    display: flex;
    align-items: center;
    gap: 10px;
    background: white;
    padding: 8px 16px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
}

.filter-pill label {
    font-size: var(--fs-label);
    font-weight: 700;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: var(--tracking-label);
}

.filter-pill select {
    border: none;
    background: transparent;
    font-size: 0.9rem;
    font-weight: 600;
    color: #0071C5;
    outline: none;
    cursor: pointer;
    min-width: 150px;
    font-family: var(--font-sans);
}

.table-glass-card {
    background: white;
    border-radius: 20px;
    border: 1px solid #eef2f7;
    overflow: hidden;
    box-shadow: 0 6px 20px rgba(15, 23, 42, 0.04);
}

.premium-table {
    width: 100%;
    border-collapse: collapse;
}

.premium-table thead {
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
}

.premium-table th {
    padding: 1.1rem 1.5rem;
    text-align: left;
    color: var(--text-secondary);
    font-size: var(--fs-th);
    font-weight: var(--fw-th);
    text-transform: uppercase;
    letter-spacing: var(--tracking-th);
}

.premium-table td {
    padding: 1.1rem 1.5rem;
    border-bottom: 1px solid #f1f5f9;
}

.table-row-hover { transition: background 0.2s; }
.table-row-hover:hover { background: #f8fafc; }

.file-name-cell {
    color: var(--text-primary);
    font-weight: 700;
    font-size: var(--fs-row-title);
}

.cell-secondary {
    color: var(--text-secondary);
    font-size: var(--fs-body);
}

.category-tag {
    font-size: var(--fs-caption);
    padding: 3px 10px;
    background: #f1f5f9;
    color: #0071C5;
    border-radius: 6px;
    font-weight: 700;
    text-transform: uppercase;
}

.status-badge {
    padding: 4px 10px;
    border-radius: 20px;
    font-size: var(--fs-caption);
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.status-badge.active { background: rgba(16, 185, 129, 0.1); color: #10b981; }

.status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
}

.empty-state {
    padding: 4rem;
    text-align: center;
    color: #94a3b8;
}

.empty-state svg { opacity: 0.3; margin-bottom: 1rem; }

.empty-state p {
    font-size: var(--fs-body-lg);
    font-weight: 500;
}

@media (max-width: 720px) {
    .pa-header { flex-direction: column; }
    .pa-filters { width: 100%; }
    .filter-pill { flex: 1; }
}
</style>
